// RF Admin 控制台壳(已登录后所有 /rf-admin/* 页面共用)。
// Header 复用主 admin 样式(globals.css .header / .header-title / .header-user)。
// 2026-09-19:背景 + Header 抄 /login-page + /dashboard 的风格。

import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, ShieldCheck, Users, UserCog, Building2 } from "lucide-react";
import { rfAdminLogout } from "../api/client";

interface RfAdminAppProps {
  admin: {
    id: number;
    email: string;
    role: "super" | "sub";
  };
  children: ReactNode;
}

const PAGE_META: Record<string, { title: string; iconClass: string; icon: ReactNode }> = {
  "/rf-admin/tenants": { title: "Tenants", iconClass: "rf-admin", icon: <Building2 size={18} /> },
  "/rf-admin/admins": { title: "Admins", iconClass: "rf-admin", icon: <UserCog size={18} /> },
  "/rf-admin/me": { title: "Profile", iconClass: "rf-admin", icon: <ShieldCheck size={18} /> },
};

function getPageMeta(pathname: string) {
  if (PAGE_META[pathname]) return PAGE_META[pathname];
  if (pathname.startsWith("/rf-admin/tenants/")) {
    return { title: "Tenant Detail", iconClass: "rf-admin", icon: <Building2 size={18} /> };
  }
  if (pathname.startsWith("/rf-admin/tenants")) {
    return { title: "Tenants", iconClass: "rf-admin", icon: <Building2 size={18} /> };
  }
  return { title: "RF Admin", iconClass: "rf-admin", icon: <ShieldCheck size={18} /> };
}

export function RfAdminApp({ admin, children }: RfAdminAppProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const meta = getPageMeta(location.pathname);
  const initial = admin.email.trim().charAt(0).toUpperCase();

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  async function onLogout() {
    await rfAdminLogout();
    navigate("/rf-admin/login", { replace: true });
  }

  return (
    <div className="rf-admin-shell">
      <main className="rf-admin-main">
        <header className="header">
          <div className="header-left">
            <div className="header-title-row">
              <span
                className={`header-title-icon header-title-icon--${meta.iconClass}`}
                aria-hidden="true"
              >
                {meta.icon}
              </span>
              <h1 className="header-title">{meta.title}</h1>
            </div>
          </div>

          <div className="header-right">
            <nav className="rf-admin-header-nav" aria-label="RF Admin nav">
              <Link to="/rf-admin/tenants" className="rf-admin-header-nav-link">
                <Building2 size={14} /> Tenants
              </Link>
              {admin.role === "super" && (
                <Link to="/rf-admin/admins" className="rf-admin-header-nav-link">
                  <Users size={14} /> Admins
                </Link>
              )}
            </nav>

            <div className="header-user-wrapper" ref={menuRef}>
              <div
                className="header-user"
                onClick={() => setMenuOpen((v) => !v)}
                role="button"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <div className="header-user-avatar">{initial}</div>
                <span className="header-user-name">{admin.email}</span>
                <ChevronDown size={14} color="#6b7280" />
              </div>

              {menuOpen && (
                <div className="header-user-menu" role="menu">
                  <div className="header-user-menu-info">
                    <div className="header-user-menu-email">{admin.email}</div>
                    <div className="header-user-menu-role">
                      Role: {admin.role === "super" ? "Super Admin" : "Sub Admin"}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="header-user-menu-item header-user-menu-item-danger"
                    onClick={onLogout}
                  >
                    <LogOut size={14} />
                    <span>ログアウト</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {children}
      </main>
    </div>
  );
}